// src/components/aac/AacButton.js
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAccessibility } from '../../contexts/AccessibilityContext';
import { isAnyModalOpen } from '../../hooks/useModalDialog';
import './AacButton.css';

const isTypingTarget = (el) => {
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
};

/**
 * Big, high-contrast button for AAC users. Optionally registers a
 * document-level number-key shortcut (1-9) that activates the same
 * onClick handler, shown as a visible badge. The shortcut listener is
 * disabled while focus is inside a text input/textarea so it never
 * hijacks digit entry (e.g. typing a password), and while any modal is
 * open so it can't reach behind the modal's focus trap.
 */
const AacButton = ({
  children,
  onClick,
  shortcut = undefined,
  type = 'button',
  variant = 'primary',
  disabled = false,
  ...rest
}) => {
  const { showShortcutBadges } = useAccessibility();

  useEffect(() => {
    if (!shortcut || disabled) return undefined;

    const handleKeyDown = (event) => {
      if (isAnyModalOpen()) return;
      if (isTypingTarget(document.activeElement)) return;
      if (event.key === String(shortcut)) {
        event.preventDefault();
        onClick(event);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [shortcut, disabled, onClick]);

  return (
    <button
      type={type}
      className={`aac-button aac-button--${variant}`}
      onClick={onClick}
      disabled={disabled}
      aria-keyshortcuts={shortcut ? String(shortcut) : undefined}
      {...rest}
    >
      <span className="aac-button__label">{children}</span>
      {shortcut && showShortcutBadges && (
        <span className="aac-button__badge" aria-hidden="true">{shortcut}</span>
      )}
    </button>
  );
};

AacButton.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired,
  shortcut: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9]),
  type: PropTypes.oneOf(['button', 'submit']),
  variant: PropTypes.oneOf(['primary', 'secondary']),
  disabled: PropTypes.bool,
};

export default AacButton;
